import React from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { STANDARDS, StandardId, Standing, standingCaption } from './types';
import * as fmt from '../../lib/format';

interface Props {
  standard: StandardId;
  standing: Standing | null;
  /** Names the quarter in the Planning caption. */
  planningQuarter?: string | null;
  /** Pressing the chip. Without it the chip is plain text. */
  onPress?: () => void;
  /** What pressing does, for screen readers: "Open Naming for AB". */
  pressLabel?: string;
  /** Set where the chip picks one of several views (My audit); leave it out where pressing opens a page. */
  selected?: boolean;
  /** A bigger number, for the overall figure. */
  large?: boolean;
}

// The change since last month: an arrow and the points. Falling is amber, not red: it is a nudge.
export function ChangeMark({ change }: { change: number | null }) {
  const words = fmt.pointsInWords(change);
  if (words === null) return null;
  const steady = words === 'steady';
  const up = (change ?? 0) > 0;
  const Icon = steady ? Minus : up ? ArrowUp : ArrowDown;
  const tone = steady ? 'text-gray-500' : up ? 'text-emerald-700' : 'text-amber-700';
  return (
    <span className={`inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-xs font-medium tabular-nums ${tone}`}>
      <Icon className="h-3 w-3" aria-hidden />
      {steady ? 'Steady' : `${Math.abs(Math.round(change as number))} pt${Math.round(Math.abs(change as number)) === 1 ? '' : 's'}`}
      <span className="sr-only"> since last month: {words}</span>
    </span>
  );
}

// One standard for one month: the percentage, how it moved since last month, and what is behind it.
export default function StandingChip({ standard, standing, planningQuarter, onPress, pressLabel, selected, large = false }: Props) {
  const info = STANDARDS.find((s) => s.id === standard)!;
  const pct = standing?.pct ?? null;
  const caption = standingCaption(standard, standing, planningQuarter);
  const body = (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">{info.label}</span>
        <ChangeMark change={standing?.change ?? null} />
      </div>
      <div className={`mt-1 font-semibold tabular-nums ${large ? 'text-4xl tracking-tight' : 'text-2xl tracking-tight'} ${pct === null ? 'text-gray-500' : 'text-gray-900'}`}>{fmt.percent(pct)}</div>
      <div className="mt-1 text-xs leading-snug text-gray-500">{caption}</div>
    </>
  );
  const base = 'block w-full rounded-xl p-3 text-left ring-1 ring-inset';
  if (!onPress) {
    return <div className={`${base} bg-gray-50/80 ring-gray-200/70`}>{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={onPress}
      aria-pressed={selected === undefined ? undefined : selected}
      aria-label={`${info.label}: ${pct === null ? 'nothing judged' : `${pct} percent`}${fmt.pointsInWords(standing?.change ?? null) ? `, ${fmt.pointsInWords(standing?.change ?? null)} since last month` : ''}. ${caption}.${pressLabel ? ` ${pressLabel}.` : ''}`}
      className={`${base} transition-[transform,box-shadow,background-color] duration-150 ease-out active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 contrast-more:ring-gray-400 ${
        selected === true ? 'bg-white shadow-sm ring-2 ring-blue-500' : 'bg-gray-50/80 ring-gray-200/70 hover:bg-white hover:shadow-sm hover:ring-gray-300'
      }`}
    >
      {body}
    </button>
  );
}
